/**
 * SowStudioPage - the full-page, de-chromed SOW Template Studio (behind the
 * SOW_STUDIO feature flag). Loads the category's SOW templates, lets the admin
 * pick one, and renders the document-first studio: a left outline, the document
 * canvas, and a contextual inspector, with the full draft/publish lifecycle,
 * autosave, stale-write handling, and the blocking conformance gate.
 *
 * Reuses the existing engine (logical sections, contract build, the per-type
 * editors, the cover/brand + settings panels) - the experience is redesigned,
 * the persistence + contract are unchanged.
 */

import * as React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, type Editor } from '@protoss/ui';
import { ResizableDivider } from '../components/ResizableDivider';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import { humanizeToken } from '../components/doc/previewSample';
import { DocumentCanvas } from '../features/sales/components/sow-studio/DocumentCanvas';
import { DocumentInspector } from '../features/sales/components/sow-studio/DocumentInspector';
import { ProofCanvas } from '../features/sales/components/sow-studio/ProofCanvas';
import { SectionInspector } from '../features/sales/components/sow-studio/SectionInspector';
import { StudioOutline } from '../features/sales/components/sow-studio/StudioOutline';
import { StudioTopBar } from '../features/sales/components/sow-studio/StudioTopBar';
import { useSowStudio } from '../features/sales/components/sow-studio/useSowStudio';
import { SowMockDataEditor } from '../features/sales/components/sow-template/SowMockDataEditor';
import type { SowWorkspaceBrand } from '../features/sales/components/sow-template/SowCoverBrandPanel';
import {
  fetchSowTemplates,
  fetchTokenCatalog,
  setWorkspaceBrandColor,
  type CatalogToken,
  type PublishSowTemplateResult,
  type SowStudioTemplate,
} from '../features/sales/lib/sowTemplateApi';
import { findUnboundTokens } from '../features/sales/lib/sowTokenValidity';
import './sow-studio-page.css';

export function SowStudioPage() {
  const { productUuid, categoryKey } = useParams<{ productUuid: string; categoryKey: string }>();
  const [searchParams] = useSearchParams();
  const wantUuid = searchParams.get('template');
  const { workspace } = useWorkspace();
  const isAdmin = useWorkspaceAdmin(workspace);

  const [templates, setTemplates] = React.useState<SowStudioTemplate[] | null>(null);
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  const workspaceBrand: SowWorkspaceBrand = React.useMemo(
    () => ({
      headerLogoAssetUuid: workspace?.headerLogoAssetUuid ?? null,
      documentLogoAssetUuid: workspace?.documentLogoAssetUuid ?? null,
      coverBackgroundAssetUuid: workspace?.coverBackgroundAssetUuid ?? null,
      footerLogoAssetUuid: workspace?.footerLogoAssetUuid ?? null,
      primaryColor: workspace?.brandPrimaryColor ?? null,
    }),
    [workspace]
  );

  const reload = React.useCallback(async (): Promise<SowStudioTemplate[]> => {
    if (!productUuid) return [];
    try {
      const all = await fetchSowTemplates(productUuid);
      // Templates for this category (its own rows + generic/null-category ones).
      const scoped = all.filter((t) => t.categoryKey === categoryKey || t.categoryKey == null);
      setTemplates(scoped);
      setLoadError(null);
      return scoped;
    } catch (err) {
      setLoadError((err as Error).message);
      setTemplates([]);
      return [];
    }
  }, [productUuid, categoryKey]);

  React.useEffect(() => {
    void reload().then((scoped) => {
      setSelectedId((prev) => {
        if (prev && scoped.some((t) => t.id === prev)) return prev;
        const byUuid = wantUuid ? scoped.find((t) => t.uuid === wantUuid) : undefined;
        return (byUuid ?? scoped.find((t) => t.isDefault) ?? scoped[0])?.id ?? null;
      });
    });
  }, [reload, wantUuid]);

  const selected = React.useMemo(
    () => templates?.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId]
  );
  const backHref = `/administration/products/${productUuid}/categories/${categoryKey}`;

  if (!productUuid || !categoryKey) {
    return <StudioMessage>Missing product or category.</StudioMessage>;
  }
  if (workspace && !isAdmin) {
    return <StudioMessage>You need workspace admin access to edit SOW templates.</StudioMessage>;
  }
  if (templates === null) {
    return <StudioMessage>Loading studio…</StudioMessage>;
  }
  if (loadError) {
    return (
      <StudioMessage>
        {loadError} <Link to={backHref}>Go back</Link>
      </StudioMessage>
    );
  }
  if (!selected) {
    return (
      <StudioMessage>
        No SOW template for this category. <Link to={backHref}>Go back</Link>
      </StudioMessage>
    );
  }

  return (
    <StudioShell
      key={selected.id}
      productUuid={productUuid}
      categoryKey={categoryKey}
      backHref={backHref}
      template={selected}
      templates={templates}
      workspaceBrand={workspaceBrand}
      onSelectTemplate={setSelectedId}
      onReload={reload}
    />
  );
}

function StudioMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="sow-studio sow-studio--message">
      <p className="sow-studio__message">{children}</p>
    </div>
  );
}

interface StudioShellProps {
  productUuid: string;
  categoryKey: string;
  backHref: string;
  template: SowStudioTemplate;
  templates: SowStudioTemplate[];
  workspaceBrand: SowWorkspaceBrand;
  onSelectTemplate: (id: number) => void;
  onReload: () => Promise<SowStudioTemplate[]>;
}

const STUDIO_RAIL_MIN = 200;
const STUDIO_RAIL_MAX = 520;

function readRailWidth(side: 'left' | 'right', fallback: number): number {
  try {
    const raw = window.localStorage?.getItem(`sow.studio.${side}Width`);
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) ? Math.min(STUDIO_RAIL_MAX, Math.max(STUDIO_RAIL_MIN, n)) : fallback;
  } catch {
    return fallback;
  }
}

function persistRailWidth(side: 'left' | 'right', w: number): void {
  try {
    window.localStorage?.setItem(`sow.studio.${side}Width`, String(w));
  } catch {
    /* ignore */
  }
}

function clampRail(w: number): number {
  return Math.min(STUDIO_RAIL_MAX, Math.max(STUDIO_RAIL_MIN, w));
}

function StudioShell({
  productUuid,
  categoryKey,
  backHref,
  template,
  templates,
  workspaceBrand,
  onSelectTemplate,
  onReload,
}: StudioShellProps) {
  const navigate = useNavigate();
  const studio = useSowStudio({ productUuid, template, workspaceBrand });
  const [mockOpen, setMockOpen] = React.useState(false);
  const [fidelityMode, setFidelityMode] = React.useState<'design' | 'proof'>('design');
  const [catalogTokens, setCatalogTokens] = React.useState<CatalogToken[]>([]);
  const [activeEditor, setActiveEditor] = React.useState<Editor | null>(null);
  const [leftWidth, setLeftWidth] = React.useState(() => readRailWidth('left', 288));
  const [rightWidth, setRightWidth] = React.useState(() => readRailWidth('right', 336));

  React.useEffect(() => {
    let active = true;
    void fetchTokenCatalog(productUuid, categoryKey)
      .then((t) => {
        if (active) setCatalogTokens(t);
      })
      .catch(() => {
        /* palette degrades to empty */
      });
    return () => {
      active = false;
    };
  }, [productUuid, categoryKey]);

  const insertToken = React.useCallback(
    (t: CatalogToken) => {
      if (!activeEditor) return;
      activeEditor
        .chain()
        .focus()
        .insertContent({
          type: 'protossToken',
          attrs: {
            token: `protoss:context.${t.token}`,
            label: humanizeToken(t.token),
            source: t.source === 'data' ? 'data' : 'context',
          },
        })
        .run();
    },
    [activeEditor]
  );

  const catalogKeys = React.useMemo(
    () => new Set(catalogTokens.map((t) => t.token)),
    [catalogTokens]
  );
  const unbound = React.useMemo(
    () => (catalogTokens.length > 0 ? findUnboundTokens(studio.draftContract, catalogKeys) : []),
    [studio.draftContract, catalogKeys, catalogTokens.length]
  );
  const [publishBlocked, setPublishBlocked] = React.useState<
    Extract<PublishSowTemplateResult, { code: 'PUBLISH_BLOCKED' }> | null
  >(null);
  const [publishError, setPublishError] = React.useState<string | null>(null);
  const [brandNote, setBrandNote] = React.useState<string | null>(null);

  const selectedSection = studio.sections.find((s) => s.id === studio.selectedId) ?? null;

  const promoteBrandColor = React.useCallback(
    async (hex: string) => {
      try {
        await setWorkspaceBrandColor(hex);
        studio.normalizeBrandColors(); // the template now tracks the new brand via the token
        setBrandNote(`Workspace brand set to #${hex}. Reload to update it everywhere.`);
      } catch (err) {
        setBrandNote((err as Error).message);
      }
    },
    [studio]
  );

  // Warn before leaving with an in-flight unsaved edit.
  React.useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (studio.isDirty && studio.canEdit) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [studio.isDirty, studio.canEdit]);

  const doPublish = React.useCallback(
    async (force: boolean) => {
      setPublishError(null);
      const res = await studio.publish(force);
      if (res.ok) {
        setPublishBlocked(null);
        await onReload();
      } else if (res.code === 'PUBLISH_BLOCKED') {
        setPublishBlocked(res);
      } else {
        setPublishError(res.message);
      }
    },
    [studio, onReload]
  );

  const doDuplicate = React.useCallback(async () => {
    const uuid = await studio.duplicateTemplate();
    const next = await onReload();
    const created = uuid ? next.find((t) => t.uuid === uuid) : undefined;
    if (created) onSelectTemplate(created.id);
  }, [studio, onReload, onSelectTemplate]);

  return (
    <div
      className="sow-studio"
      style={
        {
          '--studio-rail-left': `${leftWidth}px`,
          '--studio-rail-right': `${rightWidth}px`,
        } as React.CSSProperties
      }
    >
      <StudioTopBar
        backHref={backHref}
        backLabel="Back to category"
        templates={templates.map((t) => ({ id: t.id, label: t.label }))}
        selectedTemplateId={template.id}
        onSelectTemplate={onSelectTemplate}
        label={template.label}
        status={template.status}
        isDefault={template.isDefault}
        canEdit={studio.canEdit}
        isDirty={studio.isDirty}
        saveStatus={studio.saveStatus}
        publishing={studio.publishing}
        fidelityMode={fidelityMode}
        onFidelityModeChange={setFidelityMode}
        previewMode={studio.previewMode}
        onPreviewModeChange={studio.setPreviewMode}
        onSave={() => void studio.saveNow()}
        onPublish={() => void doPublish(false)}
        onDuplicate={() => void doDuplicate()}
      />

      {studio.stale ? (
        <Banner tone="warning">
          <span>This template was changed elsewhere since you opened it.</span>
          <span className="sow-studio-banner__actions">
            <Button type="button" variant="outline" size="sm" onClick={() => navigate(0)}>
              Reload
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => void studio.overwriteStale()}>
              Overwrite
            </Button>
          </span>
        </Banner>
      ) : null}
      {publishBlocked ? (
        <Banner tone="danger">
          <span>{publishBlocked.message}</span>
          <span className="sow-studio-banner__actions">
            <Button type="button" variant="destructive" size="sm" onClick={() => void doPublish(true)}>
              Publish anyway
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setPublishBlocked(null)}>
              Dismiss
            </Button>
          </span>
        </Banner>
      ) : null}
      {publishError ? (
        <Banner tone="danger">
          <span>{publishError}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setPublishError(null)}>
            Dismiss
          </Button>
        </Banner>
      ) : null}
      {brandNote ? (
        <Banner tone="info">
          <span>{brandNote}</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setBrandNote(null)}>
            Dismiss
          </Button>
        </Banner>
      ) : null}
      {!studio.contractReadable ? (
        <Banner tone="warning">
          <span>This template&apos;s contract could not be read - editing is disabled.</span>
        </Banner>
      ) : null}
      {unbound.length > 0 ? (
        <Banner tone="warning">
          <span>
            {unbound.length} token{unbound.length === 1 ? '' : 's'} won&apos;t resolve at deal time:{' '}
            {unbound
              .slice(0, 6)
              .map((t) => t.key)
              .join(', ')}
            {unbound.length > 6 ? '…' : ''}. Fix or remove them before publishing.
          </span>
        </Banner>
      ) : null}

      <div className="sow-studio__body">
        <aside className="sow-studio__rail sow-studio__rail--left">
          <StudioOutline
            sections={studio.sections}
            selectedId={studio.selectedId}
            canEdit={studio.canEdit}
            onSelect={studio.setSelectedId}
            onReorder={studio.reorder}
            onMove={studio.move}
            onDuplicate={studio.duplicate}
            onRemove={studio.remove}
            onAdd={studio.addSectionOfType}
          />
        </aside>

        <ResizableDivider
          direction="vertical"
          ariaLabel="Resize outline"
          onResize={(d) =>
            setLeftWidth((w) => {
              const next = clampRail(w + d);
              persistRailWidth('left', next);
              return next;
            })
          }
        />

        <main className="sow-studio__canvas-wrap">
          {fidelityMode === 'proof' ? (
            <ProofCanvas
              contract={studio.draftContract}
              sample={studio.sample}
              assetSlots={studio.resolvedAssetSlots}
              brandColor={workspaceBrand.primaryColor}
            />
          ) : (
            <DocumentCanvas
              sections={studio.sections}
              sample={studio.sample}
              headings={studio.headings}
              previewMode={studio.previewMode}
              selectedId={studio.selectedId}
              canEdit={studio.canEdit}
              onSelect={studio.setSelectedId}
              onRename={studio.rename}
              onChangeSection={studio.changeSection}
              onActiveEditor={setActiveEditor}
            />
          )}
        </main>

        <ResizableDivider
          direction="vertical"
          ariaLabel="Resize inspector"
          onResize={(d) =>
            setRightWidth((w) => {
              const next = clampRail(w - d);
              persistRailWidth('right', next);
              return next;
            })
          }
        />

        <aside className="sow-studio__rail sow-studio__rail--right">
          {selectedSection ? (
            <SectionInspector
              section={selectedSection}
              canEdit={studio.canEdit}
              sample={studio.sample}
              headings={studio.headings}
              catalogTokens={catalogTokens}
              paletteDisabled={!activeEditor}
              onSwitchType={studio.switchType}
              onChangeSection={studio.changeSection}
              onSampleChange={studio.setSample}
              onInsertToken={insertToken}
            />
          ) : (
            <DocumentInspector
              draftContract={studio.draftContract}
              assetSlots={studio.resolvedAssetSlots}
              workspaceBrand={workspaceBrand}
              visualOverrides={studio.visualOverrides}
              settings={studio.settings}
              pageChrome={studio.pageChrome}
              canEdit={studio.canEdit}
              literalAccentColorCount={studio.literalAccentColorCount}
              dominantOffBrandColor={studio.dominantOffBrandColor}
              onVisualOverridesChange={studio.setVisualOverrides}
              onSettingsChange={studio.setSettings}
              onPageChromeChange={studio.setPageChrome}
              onNormalizeBrandColors={studio.normalizeBrandColors}
              onPromoteBrandColor={(hex) => void promoteBrandColor(hex)}
              onEditData={() => setMockOpen(true)}
            />
          )}
        </aside>
      </div>

      <SowMockDataEditor
        open={mockOpen}
        sample={studio.sample}
        onClose={() => setMockOpen(false)}
        onChange={studio.setSample}
      />
    </div>
  );
}

function Banner({
  tone,
  children,
}: {
  tone: 'warning' | 'danger' | 'info';
  children: React.ReactNode;
}) {
  return <div className={`sow-studio-banner sow-studio-banner--${tone}`}>{children}</div>;
}
